import {PolymerElement} from '@polymer/polymer/polymer-element.js';
import {html} from '@polymer/polymer/lib/utils/html-tag.js';
import '../ns-form/ns-form.js';
import template from './ns-select.html';
import {FormMixin} from '../formMixin.js';

/**
 * Used when a state is needed to be selected.
 * This component validates if a field has been chosen.
 *
 * <section>
 *  <h2>Content Guidelines</h2>
 *  <p>Here are some guidelines on how to properly use this element.</p>
 * </section>
 *
 * <h2>State Component Demo</h2>
 * <demo-snippet class="demoSnippet">
 *   <template>
 *     <ns-state></ns-state>
 *   </template>
 * </demo-snippet>
 */
export class NsSelect extends FormMixin(PolymerElement) {
    static get is() {
        return 'ns-select';
    }

    static get template() {
        let htmlElement = document.createElement('TEMPLATE');
        htmlElement.innerHTML = template;
        return htmlElement;
    }

    static get properties() {
        return {
            /**
             * optionList
             * Holds the list of values
             * [{ key: "keyvalue", value: "value"}]
             */
            optionList: {
                type: Array,
                observer: 'optionsChanged'
            },
            /**
             * label
             * Used to indicate label for localization
             */
            label: {
                type: String
            },
            /**
             * placeholder
             */
            placeholder: {
                type: String
            },
            /**
             * The value selected in the dropdown.
             */
            value: {
                type: String,
                notify: true,
                reflectToAttribute: true,
                observer: 'changed'
            },
            /**
             * required
             * Displays an error if the user doesn't select a value
             */
            required: {
                type: Boolean
            },
            /**
             * error-message
             * Message displayed when user doesn't select a value
             */
            errorMessage: {
                type: String
            },
            /**
             * Flag representing the dirty state of this component
             */
            dirty: {
                type: Boolean,
                value: false,
                notify: true,
                reflectToAttribute: true
            },
            /**
             * This should not be passed in but the attribute must be there to show and hide errors
             */
            showError: {
                type: Boolean,
                value: false,
                reflectToAttribute: true
            }
        };
    }

    static get observers() {
        return [
            '_updateDirtyFlag(stateDirty)'
        ];
    }

    /**
     * Ready Function
     * When component is initialized it will load the translations and state list
     * translations and the state list are pulled based on country and language
     */
    ready() {
        super.ready();
    }

    /**
     * Called when the country or language changes
     * @private
     */
    _initialize() {
    }

    focus() {
        if (!this.opened) {
            this.opened = true;
            this.originalValue = this.value;
        }
    }

    changed() {
        if (this.opened) {
            if (this.originalValue !== this.value) {
                this.dirty = true;
            } else {
                this.dirty = false;
            }
        }
    }

    optionsChanged() {
        if (this.optionList && this.optionList.length > 0) {
            //When editing an address if you set the value before the list is rendered in the dom then it wont set the value correctly
            // Even when the list is set the dom hasn't rendered so we need to sleep before resetting the value
            setTimeout(() => {
                let value = this.value;
                this.value = '';
                this.value = value;
            }, 300);
        }
    }

    blur() {
        this.validate();
    }

    clear() {
        this.opened = false;
        this.value = '';
        this.originalValue = '';
        this.dirty = false;
        this.valid = true;
        this.errorClass = '';
        this.showError = false;
    }

    /**
     * Form validation - ns-form looks for this function for validation
     */
    validate() {
        this.valid = true;
        if (!(this.attributes.style && this.attributes.style.textContent.indexOf('display: none') > -1)) {
            if (this.required && !this.value) {
                this.valid = false;
                this.showError = true;
                this.errorClass = 'error';
            } else {
                this.showError = false;
                this.errorClass = '';
            }
        }
        return this.valid;
    }
}

customElements.define(NsSelect.is, NsSelect);
